import { Location } from '@angular/common';
import { Component, Inject, QueryList, ViewChildren } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ActivityBodyInterface,
  ActivityInterface,
} from '@sport-activities/sdk';

import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { FormInputComponent } from '~/component/form/input/input.component';
import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';

import { OrganizerActivityCreationPageEquipmentPagesInterface } from './interfaces/pages.interface';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';

const pagesConfig: OrganizerActivityCreationPageEquipmentPagesInterface = {
  'organizer.activity-creation.equipment-provided': {
    addButtonKey:
      'activity_creation.equipment-provided.equipment-provided.add-button',
    apiFieldName: 'providedEquipment',
    cssClass: 'organizer-activity-creation-page-equipment-provided',
    deleteButtonAriaLabelKey:
      'activity_creation.equipment-provided.equipment-provided.delete-button',
    formControlTitleKey:
      'activity_creation.equipment-provided.equipment-provided.title',
    pageTitleKey: 'seo.organizer.activity_creation.equipment_provided.title',
    sectionDescriptionKey:
      'activity_creation.equipment-provided.section.description',
    sectionTitleKey: 'activity_creation.equipment-provided.section.title',
  },
  'organizer.activity-creation.equipment-required': {
    addButtonKey:
      'activity_creation.equipment-required.equipment-required.add-button',
    apiFieldName: 'requiredEquipment',
    cssClass: 'organizer-activity-creation-page-equipment-required',
    deleteButtonAriaLabelKey:
      'activity_creation.equipment-required.equipment-required.delete-button',
    formControlTitleKey:
      'activity_creation.equipment-required.equipment-required.title',
    pageTitleKey: 'seo.organizer.activity_creation.equipment_required.title',
    sectionDescriptionKey:
      'activity_creation.equipment-required.section.description',
    sectionTitleKey: 'activity_creation.equipment-required.section.title',
  },
};

@Component({
  selector: 'app-organizer-activity-creation-equipment',
  templateUrl: './equipment.component.html',
  styleUrls: [
    '../../../../../organizer/shared/components/shared/form-wrapper/form-wrapper.component.scss',
  ],
})
export class OrganizerActivityCreationPageEquipmentComponent extends AbstractOrganizerActivityCreationFormPageDirective {
  @ViewChildren('equipmentInputs')
  public inputComponents: QueryList<FormInputComponent>;

  public readonly addButtonIconType = VtmnPlayIconTypeEnum.Add;
  public readonly addButtonType = CtaButtonTypeEnum.Button;
  public readonly addButtonVariant = CtaVariantEnum.Link2;
  public readonly crossIconType = VtmnPlayIconTypeEnum.Close;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;

  protected pageTitleKey = '';
  public addButtonKey: string;
  public apiFieldName: string;
  public cssClass: string;
  public deleteButtonAriaLabelKey: string;
  public formControlTitleKey: string;
  public sectionDescriptionKey: string;
  public sectionTitleKey: string;

  constructor(
    private readonly location: Location,
    private readonly locatableService: OrganizerSharedLocatableService,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  public get equipmentFormArray(): FormArray {
    return this.formGroup.get('equipments') as FormArray;
  }

  public get equipmentFormControls(): FormControl[] {
    return this.equipmentFormArray.controls as FormControl[];
  }

  private buildPage(): void {
    const currentName = NavigationService.getCurrentName(this.activatedRoute);
    const pageConfig = pagesConfig[currentName];

    if (!pageConfig) {
      this.location.back();

      return;
    }

    this.addButtonKey = pageConfig.addButtonKey;
    this.apiFieldName = pageConfig.apiFieldName;
    this.cssClass = pageConfig.cssClass;
    this.deleteButtonAriaLabelKey = pageConfig.deleteButtonAriaLabelKey;
    this.formControlTitleKey = pageConfig.formControlTitleKey;
    this.sectionDescriptionKey = pageConfig.sectionDescriptionKey;
    this.sectionTitleKey = pageConfig.sectionTitleKey;
    this.pageTitleKey = pageConfig.pageTitleKey;
  }

  private cleanEquipments(): string[] {
    return this.equipmentFormControls
      .map((control) => control.value.name)
      .filter((name) => name); // Clear empty string.
  }

  private setDefaultData(): void {
    const activity = this.activityService.activity;

    (activity[this.apiFieldName] || [])
      .map((equipment) => this.createEquipment(equipment))
      .forEach((equipmentFormControl) => {
        this.equipmentFormArray.push(equipmentFormControl);
      });
  }

  protected beforeInitHook(): void {
    this.buildPage();
  }

  protected buildActivityData(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    return {
      [this.apiFieldName]: this.cleanEquipments(),
    };
  }

  protected buildDataBeforeSubmit(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityBodyInterface> {
    return {
      [this.apiFieldName]: [
        this.locatableService.buildCurrentLocatableBody<string[]>(
          this.cleanEquipments()
        ),
      ],
    };
  }

  protected buildValidationConfig(): void {
    this.validationConfig = {
      equipments: this.formBuilder.array([]),
    };
  }

  protected onInitHook(): void {
    this.setDefaultData();
  }

  public addEquipment($event?: Event): void {
    $event?.preventDefault();

    this.equipmentFormArray.push(this.createEquipment());
    this.focusEquipmentInput(this.equipmentFormArray?.length - 1);
  }

  public createEquipment(name = ''): FormGroup {
    return this.formBuilder.group(
      {
        name,
      },
      {
        validators: noHtmlTagValidator(),
      }
    );
  }

  public deleteEquipment(equipmentIndex: number): void {
    this.equipmentFormArray.removeAt(equipmentIndex);
  }

  public focusEquipmentInput(index: number): void {
    /**
     * setTimeout is used to prevent changes after checked
     */
    setTimeout(() => {
      this.inputComponents.forEach((input, inputIndex) => {
        if (inputIndex === index) {
          input.focus();
        }
      });
    });
  }

  public getErrors(input: FormControl): ValidationErrors | null | false {
    return input?.touched && input?.errors;
  }

  public getLabelName(index: number): string {
    return `${this.apiFieldName}-${index}`;
  }

  public submit(formGroupValue: FormGroupValueInterface): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    super.submit(formGroupValue);
  }
}
